import React from 'react';
import Collapse from '@material-ui/core/Collapse';
import Tooltip from '@material-ui/core/Tooltip';
import {
    VpnKey,
    Memory,
    EmojiObjects,
    Timeline,
    AccessTime,
    Input as InputIcon,
    Launch,
    AttachMoney,
    Person,
    Block,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import {
    LLM_DETAIL_CONTENT_COLUMNS,
    LLM_DETAIL_ICON_COLUMNS,
    renderDetailRow,
} from '../TracePanelShared';

const resolveLlmMetrics = (contextItems, llmMetrics) => {
    if (llmMetrics) {
        return llmMetrics;
    }

    const inputTokenCountItem = contextItems.find(
        (item) => item.name === 'inputTokenCount' || item.name === 'InputTokenCount'
    );
    const outputTokenCountItem = contextItems.find(
        (item) => item.name === 'outputTokenCount' || item.name === 'OutputTokenCount'
    );
    const responseTimeItem = contextItems.find(
        (item) => item.name === 'responseTimeMillis' || item.name === 'ResponseTimeMillis'
    );

    if (inputTokenCountItem || outputTokenCountItem || responseTimeItem) {
        return {
            inputTokenCount: inputTokenCountItem ? parseInt(inputTokenCountItem.text, 10) : undefined,
            outputTokenCount: outputTokenCountItem ? parseInt(outputTokenCountItem.text, 10) : undefined,
            responseTimeMillis: responseTimeItem ? parseInt(responseTimeItem.text, 10) : undefined,
        };
    }

    return null;
};

const ConversationItemLlmFields = ({
    contextItems,
    allowedFields,
    llmMetrics,
    modelInfo,
    promptId,
    usedAsResponse,
}) => {
    const { t } = useTranslation();
    const effectiveMetrics = resolveLlmMetrics(contextItems, llmMetrics);

    const renderChipRow = (IconComponent, label, value, chipStyle = {}, iconStyle = {}) => renderDetailRow({
        spacer: (
            <span style={{ display: 'block', textAlign: 'right', paddingRight: 8 }}>
                <Tooltip title={label}>
                    <span>
                        <IconComponent style={{ fontSize: 18, color: '#555', ...iconStyle }} />
                    </span>
                </Tooltip>
            </span>
        ),
        content: (
            <span
                style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    fontSize: '0.8rem',
                    whiteSpace: 'nowrap',
                    ...chipStyle,
                }}
            >
                {value}
            </span>
        ),
        spacerColumns: LLM_DETAIL_ICON_COLUMNS,
        contentColumns: LLM_DETAIL_CONTENT_COLUMNS,
        trailingColumns: 0,
        alignItems: 'center',
        marginTop: 4,
    });

    const renderPromptFields = (contextItem, isPromptID, isUsingAgent) => {
        if (isUsingAgent) {
            return renderChipRow(Person, t('UsingAgent'), contextItem.text);
        }

        if (!isPromptID) {
            return null;
        }

        const calculateCallPrice = () => {
            if (!modelInfo || modelInfo.inputTokenCount === undefined || modelInfo.outputTokenCount === undefined) {
                return null;
            }
            const inputPrice = (modelInfo.inputTokenCount || 0) * (modelInfo.priceCzkPerInputToken || 0);
            const outputPrice = (modelInfo.outputTokenCount || 0) * (modelInfo.priceCzkPerOutputToken || 0);
            return inputPrice + outputPrice;
        };

        const callPrice = calculateCallPrice();

        return (
            <>
                {renderChipRow(VpnKey, t('PromptID'), contextItem.text)}
                {modelInfo?.name && renderChipRow(Memory, t('LLM Model'), modelInfo.name)}
                {modelInfo && renderChipRow(
                    EmojiObjects,
                    t('Thinking enabled'),
                    modelInfo.thinkingEnabled ? t('Enabled') : t('Disabled')
                )}
                {modelInfo && modelInfo.thinkingEnabled && modelInfo.thinkingLabelKey && modelInfo.thinkingValue !== undefined &&
                    renderChipRow(Timeline, t(modelInfo.thinkingLabelKey), modelInfo.thinkingValue)
                }
                {effectiveMetrics?.responseTimeMillis !== undefined &&
                    renderChipRow(
                        AccessTime,
                        t('Response Time'),
                        `${(effectiveMetrics.responseTimeMillis / 1000).toFixed(2)} s`
                    )
                }
                {effectiveMetrics?.inputTokenCount !== undefined &&
                    renderChipRow(
                        InputIcon,
                        t('Input Token Count'),
                        effectiveMetrics.inputTokenCount
                    )
                }
                {effectiveMetrics?.outputTokenCount !== undefined &&
                    renderChipRow(
                        Launch,
                        t('Output Token Count'),
                        effectiveMetrics.outputTokenCount
                    )
                }
                {callPrice !== null &&
                    renderChipRow(
                        AttachMoney,
                        t('Call Price'),
                        `${callPrice.toFixed(4)} CZK`
                    )
                }
                {usedAsResponse === false &&
                    renderChipRow(
                        Block,
                        t('Used as response'),
                        t('Not Used'),
                        {
                            backgroundColor: '#ffebee',
                            border: '1px solid #ef9a9a',
                        },
                        {
                            color: '#c75d5d',
                        }
                    )}
            </>
        );
    };

    const hasPromptIDInContext = contextItems.some((item) => item.name === 'PromptID');
    const items = contextItems.map((contextItem) => {
        if (!allowedFields.includes(contextItem.name)) {
            return null;
        }

        const isPromptID = contextItem.name === 'PromptID';
        const isUsingAgent = contextItem.name === 'UsingAgent';
        const effectiveItem = isPromptID && promptId
            ? { ...contextItem, text: promptId }
            : contextItem;

        return (
            <React.Fragment key={`${effectiveItem.name}-${effectiveItem.text}`}>
                {renderPromptFields(effectiveItem, isPromptID, isUsingAgent)}
            </React.Fragment>
        );
    });

    if (!hasPromptIDInContext && promptId) {
        items.unshift(
            <React.Fragment key={`PromptID-${promptId}`}>
                {renderPromptFields({ name: 'PromptID', text: promptId }, true, false)}
            </React.Fragment>
        );
    }

    if (!items.some(Boolean) && !promptId) {
        return null;
    }

    return (
        <Collapse in={true} className="break-words whitespace-pre-line overflow-hidden">
            {items}
        </Collapse>
    );
};

export default ConversationItemLlmFields;
